import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "http://localhost:3001";

function readLocalToken() {
  try {
    const raw = localStorage.getItem("auth");
    const obj = raw ? JSON.parse(raw) : null;
    return obj?.token || null;
  } catch {
    return null;
  }
}

function writeLocalToken(token) {
  if (!token) return;
  try {
    const raw = localStorage.getItem("auth");
    const obj = raw ? JSON.parse(raw) : null;
    // Only refresh the token on an EXISTING auth entry. Creating a new
    // `{ token }`-only entry here used to resurrect localStorage.auth after
    // Navbar deliberately cleared it (e.g. for unverified-email sessions),
    // which made the header flash a nameless "U / User" chip for visitors
    // who were never properly signed in.
    if (obj) localStorage.setItem("auth", JSON.stringify({ ...obj, token }));
  } catch {}
}

// ✅ ждём, пока Firebase восстановит пользователя (один раз)
async function waitForAuthReady(timeoutMs = 2000) {
  if (auth.currentUser) return auth.currentUser;

  return new Promise((resolve) => {
    const t = setTimeout(() => {
      unsub?.();
      resolve(auth.currentUser || null);
    }, timeoutMs);

    const unsub = onAuthStateChanged(auth, (user) => {
      clearTimeout(t);
      unsub();
      resolve(user || null);
    });
  });
}

async function getFreshToken(force = false) {
  // 1) если уже есть — берём сразу
  if (auth.currentUser) {
    const token = await auth.currentUser.getIdToken(force);
    writeLocalToken(token);
    return token;
  }

  // 2) если нет — ждём восстановление сессии
  const user = await waitForAuthReady();
  if (user) {
    const token = await user.getIdToken(force);
    writeLocalToken(token);
    return token;
  }

  // 3) fallback на localStorage (если юзер не восстановился)
  return readLocalToken();
}

/**
 * Authenticated GET that returns a Blob (e.g. a private document an admin is
 * reviewing). A plain <img src> / <iframe src> can't send the Authorization
 * header, so callers turn the Blob into an object URL instead.
 */
export async function apiFetchBlob(path) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const token = await getFreshToken(false);
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: "no-store",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const err = new Error(data?.error || res.statusText || "Request failed");
    err.status = res.status;
    throw err;
  }
  return res.blob();
}

export async function apiFetch(path, options = {}) {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const method = (options.method || "GET").toUpperCase();
  const headers = new Headers(options.headers || {});
  const body = options.body;

  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const hasBody = body !== undefined && body !== null;

  if (hasBody && !isFormData && method !== "GET" && method !== "HEAD") {
    if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  }

  let token = await getFreshToken(false);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const doReq = async () =>
    fetch(url, {
      ...options,
      method,
      headers,
      body,
    });

  let res = await doReq();

  // ✅ если 401/403 — пробуем обновить токен и повторить один раз
  if ((res.status === 401 || res.status === 403) && (auth.currentUser || readLocalToken())) {
    token = await getFreshToken(true);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    res = await doReq();
  }

  const ct = res.headers.get("content-type") || "";
  const data = ct.includes("application/json")
    ? await res.json().catch(() => null)
    : await res.text().catch(() => "");

  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && data.error) ||
      (typeof data === "string" && data) ||
      res.statusText ||
      "Request failed";

    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}
